/**
 * Settings › Customers › **Email signatures** — Aaron, Oct 24, Inbox note 2.7:
 * "a company wide email signature builder in the settings that allows merged
 * fields and have every user get assigned email signatures … in WYSIWIG and
 * HTML. Code Two's designer would be a good place to pull from."
 *
 * The settings layout (design-system §5 pattern 3): a column of cards — the
 * signatures, the one being edited, who uses which — and a 364px rail with
 * the preview, which on a narrower screen drops under them.
 *
 *  - **Signatures**: every one the company has, the default tagged, how many
 *    people send with each today. **New signature** starts from a template
 *    (CodeTwo's starting point): Simple, With logo, With branch, or Blank.
 *  - **The editor**: a name, **Visual · HTML** (`signature-editor.tsx`), and
 *    **Save**. A save says what the sanitiser took out, when it took anything.
 *  - **Who uses which**: the company default, then a signature per role, then
 *    a person's own — the rule `services/email-signatures.ts` applies, said
 *    under the card. Each change saves at once.
 *  - **Automatic emails** (Aaron, Oct 25, answer 2.6): an appointment
 *    confirmation and an invoice — what each ends with: no signature (what
 *    they always had), one of these signed as the company, or the
 *    salesperson's own.
 *  - **Preview**: the draft, rendered for whoever is picked — their name,
 *    their branch, today's logo — and which signature they really send with.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  SAMPLE_SIGNATURE_VALUES, SIGNATURE_TEMPLATES, can, renderSignature, signatureUseWords,
  type AutomaticEmailSetting, type EmailSignatureView, type EmailSignaturesPage, type MeResponse,
  type SignatureEditorMode, type SignaturePreviewFacts, type SignatureSaved, type SignatureTemplate,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Band, Button, Segmented, Tag } from '../components/primitives.js'
import { Modal, Popover } from '../components/overlays.js'
import { SettingsCard } from '../components/settings-card.js'
import { Field, SelectInput, TextInput } from '../components/form/index.js'
import { SignaturePaper } from '../components/signature-paper.js'
import { IconClose, IconPlus } from '../frame/icons.js'
import { useSettingsBar } from './settings-bar.js'
import { SignatureEditor } from './signature-editor.js'

interface Draft { name: string; html: string; editor: SignatureEditorMode }

const errorOf = (problem: unknown, fallback: string): string =>
  (problem as { message?: string }).message ?? fallback

/** A draft that says something the stored signature does not. */
const changed = (stored: EmailSignatureView, draft: Draft | undefined): boolean =>
  Boolean(draft && (draft.name !== stored.name || draft.html !== stored.html || draft.editor !== stored.editor))

const usedWords = (count: number): string =>
  (count === 0 ? 'Nobody sends with it yet' : `${count} ${count === 1 ? 'person sends' : 'people send'} with it`)

export function EmailSignaturesPage({ me }: { me: MeResponse }) {
  const canEdit = can(me.permissions, 'company_settings', 'edit')
  const [page, setPage] = useState<EmailSignaturesPage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [saving, setSaving] = useState(false)
  const [dropped, setDropped] = useState<string[]>([])
  const [saved, setSaved] = useState(false)
  const [creating, setCreating] = useState(false)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [facts, setFacts] = useState<SignaturePreviewFacts | null>(null)

  const bar = useSettingsBar({ title: 'Email signatures', sub: 'what every email a person sends ends with' })

  /* A page from the server, and a selection that still exists in it — the default after a removal. */
  const adopt = useCallback((answer: EmailSignaturesPage) => {
    setPage(answer)
    setSelected((was) => (was && answer.signatures.some((one) => one.id === was) ? was
      : answer.signatures.find((one) => one.isDefault)?.id ?? answer.signatures[0]?.id ?? null))
  }, [])

  const load = useCallback(async () => {
    try {
      const answer = await request<EmailSignaturesPage>('/settings/email-signatures')
      adopt(answer)
      setPreviewId((was) => was ?? (answer.office.some((person) => person.id === me.user.id) ? me.user.id
        : answer.office[0]?.id ?? null))
    } catch (problem) {
      setError(errorOf(problem, 'These settings did not load.'))
    }
  }, [adopt, me.user.id])
  useEffect(() => { void load() }, [load])

  /* The preview person's fields and which signature they send with — asked again when the assignments move. */
  const asked = useRef(0)
  const loadFacts = useCallback(async (userId: string) => {
    const ticket = ++asked.current
    try {
      const answer = await request<SignaturePreviewFacts>(`/settings/email-signatures/values?userId=${userId}`)
      if (ticket === asked.current) setFacts(answer)
    } catch {
      if (ticket === asked.current) setFacts(null)
    }
  }, [])
  useEffect(() => { if (previewId) void loadFacts(previewId) }, [previewId, loadFacts, page])

  const signature = page?.signatures.find((one) => one.id === selected) ?? null
  const draft: Draft | null = signature ? drafts[signature.id]
    ?? { name: signature.name, html: signature.html, editor: signature.editor } : null
  const dirty = Boolean(signature && changed(signature, drafts[signature.id]))
  const edit = (next: Partial<Draft>) => {
    if (!signature || !draft) return
    setSaved(false)
    setDrafts((all) => ({ ...all, [signature.id]: { ...draft, ...next } }))
  }

  const save = async () => {
    if (!signature || !draft) return
    setSaving(true)
    setError(null)
    try {
      const answer = await request<SignatureSaved>(`/settings/email-signatures/${signature.id}`, {
        method: 'PATCH', body: { name: draft.name, html: draft.html, editor: draft.editor, version: signature.version },
      })
      setDropped(answer.dropped)
      setSaved(true)
      setDrafts((all) => {
        const next = { ...all }
        delete next[signature.id]
        return next
      })
      await load()
    } catch (problem) {
      setError(errorOf(problem, 'That did not save.'))
    } finally {
      setSaving(false)
    }
  }

  const act = async (path: string, body: unknown, method: 'POST' | 'PUT' = 'POST') => {
    setError(null)
    try {
      adopt(await request<EmailSignaturesPage>(path, { method, body }))
    } catch (problem) {
      setError(errorOf(problem, 'That did not save.'))
    }
  }

  const values = facts?.values ?? SAMPLE_SIGNATURE_VALUES
  const preview = useMemo(() => (draft ? renderSignature(draft.html, values) : ''), [draft, values])
  const previewName = page?.office.find((person) => person.id === previewId)?.name ?? 'Somebody'
  const usesName = facts?.uses.signatureId
    ? page?.signatures.find((one) => one.id === facts.uses.signatureId)?.name ?? null : null

  if (!page) {
    return (
      <div className="px-6 py-5">
        {bar}
        <p className="m-0 text-[13px] text-text-muted">{error ?? 'Loading…'}</p>
      </div>
    )
  }

  return (
    <div className="flex items-start gap-5 p-5 max-xl:flex-col">
      {bar}
      <div className="flex min-w-0 max-w-[868px] flex-1 flex-col gap-4 max-xl:w-full max-xl:max-w-full">
        <SettingsCard title="Signatures" sub="every email a person sends ends with one"
          action={canEdit && (
            <Button className="h-[30px] text-[12.5px]" onClick={() => setCreating(true)}>
              <IconPlus size={14} />New signature
            </Button>
          )}>
          <div role="listbox" aria-label="Signatures" className="flex flex-col">
            {page.signatures.length === 0 && (
              <p className="m-0 px-[18px] py-3 text-[12.5px] text-text-muted">
                No signatures yet — emails go with the words people write and nothing under them.
              </p>
            )}
            {page.signatures.map((one) => (
              <button key={one.id} type="button" role="option" aria-selected={one.id === selected}
                onClick={() => { setSelected(one.id); setDropped([]); setSaved(false) }}
                className={cn('flex items-center gap-3 border-b border-line-faint px-[18px] py-[11px] text-left last:border-b-0',
                  one.id === selected ? 'bg-green-band-bg' : 'hover:bg-hover')}>
                <span className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-2">
                    <b className="truncate text-[13.5px] font-bold text-text">{drafts[one.id]?.name ?? one.name}</b>
                    {one.isDefault && <Tag tone="ok">Default</Tag>}
                    {changed(one, drafts[one.id]) && <Tag tone="warn">Not saved</Tag>}
                  </span>
                  <span className="text-[12px] text-text-muted">{usedWords(one.usedBy)}</span>
                </span>
              </button>
            ))}
          </div>
        </SettingsCard>

        {signature && draft && (
          <SettingsCard title={draft.name.trim() || 'Untitled signature'}
            sub={draft.editor === 'html' ? 'written in HTML' : 'written in the visual editor'}>
            <div className="flex flex-col gap-3.5 px-[18px] py-4">
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Name" className="min-w-[220px] flex-1">
                  {({ id, describedBy }) => (
                    <TextInput id={id} describedBy={describedBy} value={draft.name} disabled={!canEdit} maxLength={80}
                      onChange={(event) => edit({ name: event.target.value })} />
                  )}
                </Field>
                <Segmented size="field" label="Editor" value={draft.editor}
                  onChange={(editor) => edit({ editor })}
                  options={[{ value: 'visual', label: 'Visual' }, { value: 'html', label: 'HTML' }]} />
              </div>
              <SignatureEditor mode={draft.editor} html={draft.html} values={values} disabled={!canEdit}
                onChange={(html) => edit({ html })} />
              {dropped.length > 0 && (
                <Band tone="amber" title="Taken out on save">
                  {dropped.join(' · ')} — an email can’t carry them, or they would load something from elsewhere.
                </Band>
              )}
            </div>
            {canEdit && (
              <div className="flex flex-wrap items-center gap-2.5 border-t border-line-faint bg-surface-alt px-[18px] py-3">
                {!signature.isDefault && (
                  <Button className="h-8 text-[12.5px]"
                    onClick={() => void act('/settings/email-signatures/default', { signatureId: signature.id }, 'PUT')}>
                    Make it the default
                  </Button>
                )}
                {!signature.isDefault && (
                  <Button variant="danger" className="h-8 text-[12.5px]"
                    onClick={() => {
                      if (window.confirm(`Remove ${signature.name}? Whoever uses it goes back to the company default.`)) {
                        void act(`/settings/email-signatures/${signature.id}/remove`, {})
                      }
                    }}>
                    Remove
                  </Button>
                )}
                <span className="flex-1" />
                {saved && !dirty && <span className="text-[12px] text-text-muted">Saved</span>}
                <Button className="h-8 text-[12.5px]" disabled={!dirty || saving}
                  onClick={() => setDrafts((all) => {
                    const next = { ...all }
                    delete next[signature.id]
                    return next
                  })}>
                  Undo changes
                </Button>
                <Button variant="primary" className="h-8 text-[12.5px]" disabled={!dirty || saving}
                  onClick={() => void save()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            )}
          </SettingsCard>
        )}

        <WhoUsesWhich page={page} canEdit={canEdit}
          onAct={(body, path = '/settings/email-signatures/assignments') => void act(path, body, 'PUT')} />
        <AutomaticEmails page={page} canEdit={canEdit}
          onAct={(body) => void act('/settings/email-signatures/automatic', body, 'PUT')} />
        {error && <p role="alert" className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>

      <aside className="flex w-[364px] shrink-0 flex-col gap-3.5 max-xl:w-full">
        <SettingsCard title="Preview" sub="how it ends their email">
          <div className="flex flex-col gap-3 px-[18px] py-3.5">
            <Field label="As">
              {({ id }) => (
                <SelectInput id={id} label="Preview as" value={previewId}
                  onChange={(id) => setPreviewId(id)}
                  options={page.office.map((person) => ({ value: person.id, label: person.name,
                    ...(person.title ? { help: person.title } : {}) }))} />
              )}
            </Field>
            <div className="rounded-field border border-line px-3 py-2.5" data-signature-preview>
              {preview
                ? <SignaturePaper html={preview} title={`${draft?.name ?? 'Signature'} for ${previewName}`} />
                : <span className="text-[12.5px] text-text-faint">Nothing to show yet</span>}
            </div>
            {facts && (
              <span className="text-[12px] leading-[1.45] text-text-muted">
                {previewName} · {signatureUseWords(facts.uses, usesName)}
              </span>
            )}
            <span className="text-[11px] leading-[1.45] text-text-faint">
              A line whose fields are all empty for somebody is left out. Images come from Rowted, so every mail
              client can show them.
            </span>
          </div>
        </SettingsCard>
      </aside>

      {creating && (
        <NewSignature values={values} onClose={() => setCreating(false)}
          onMade={async (made) => {
            setCreating(false)
            await load()
            setSelected(made.id)
          }} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Who uses which
// ---------------------------------------------------------------------------

const DEFAULT = 'default'
const NONE = 'none'

function WhoUsesWhich({ page, canEdit, onAct }: {
  page: EmailSignaturesPage
  canEdit: boolean
  onAct: (body: unknown, path?: string) => void
}) {
  const [adding, setAdding] = useState(false)
  const addAt = useRef<HTMLSpanElement>(null)
  const company = page.signatures.find((one) => one.isDefault) ?? null
  const choices = (followsLabel: string) => [
    { value: DEFAULT, label: followsLabel },
    ...page.signatures.map((one) => ({ value: one.id, label: one.name })),
    { value: NONE, label: 'No signature' },
  ]
  const asChoice = (signatureId: string | null | undefined): string =>
    (signatureId === undefined ? DEFAULT : signatureId === null ? NONE : signatureId)
  const asValue = (choice: string | null): string | null => (choice === NONE ? null : choice === DEFAULT ? 'default' : choice)
  const follows = `Company default${company ? ` · ${company.name}` : ' · none'}`
  const listed = new Set(page.people.map((person) => person.userId))

  return (
    <SettingsCard title="Who uses which" sub="every person sends with one">
      <Row label="Company default" sub="whoever has nothing of their own">
        <SelectInput label="Company default" value={company?.id ?? NONE} disabled={!canEdit}
          onChange={(choice) => onAct({ signatureId: choice === NONE ? null : choice }, '/settings/email-signatures/default')}
          options={[...page.signatures.map((one) => ({ value: one.id, label: one.name })),
            { value: NONE, label: 'No signature' }]} />
      </Row>
      <p className="m-0 border-b border-line-faint bg-surface-alt px-[18px] pb-1.5 pt-2.5 text-label uppercase text-text-faint">
        By role
      </p>
      {page.roles.map((role) => (
        <Row key={role.id} label={role.name} sub={`${role.people} ${role.people === 1 ? 'person' : 'people'}`}>
          <SelectInput label={`${role.name} signature`} value={asChoice(role.signatureId)} disabled={!canEdit}
            onChange={(choice) => onAct({ roleId: role.id, signatureId: asValue(choice) })}
            options={choices(follows)} />
        </Row>
      ))}
      <p className="m-0 border-b border-line-faint bg-surface-alt px-[18px] pb-1.5 pt-2.5 text-label uppercase text-text-faint">
        By person
      </p>
      {page.people.length === 0 && (
        <p className="m-0 border-b border-line-faint px-[18px] py-2.5 text-[12.5px] text-text-muted">
          Nobody has one of their own.
        </p>
      )}
      {page.people.map((person) => (
        <Row key={person.userId} label={person.name} sub={person.title ?? ''}
          after={canEdit && (
            <button type="button" aria-label={`${person.name} follows their role again`}
              onClick={() => onAct({ userId: person.userId, signatureId: 'default' })}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-button text-text-muted hover:bg-hover">
              <IconClose size={14} />
            </button>
          )}>
          <SelectInput label={`${person.name} signature`} value={asChoice(person.signatureId)} disabled={!canEdit}
            onChange={(choice) => onAct({ userId: person.userId,
              signatureId: choice === DEFAULT ? 'default' : asValue(choice) })}
            options={[...page.signatures.map((one) => ({ value: one.id, label: one.name })),
              { value: NONE, label: 'No signature' }]} />
        </Row>
      ))}
      <div className="flex flex-wrap items-center gap-2.5 px-[18px] py-3">
        {canEdit && (
          <span ref={addAt} className="inline-flex">
            <Button className="h-[30px] text-[12.5px]" onClick={() => setAdding(true)}>
              <IconPlus size={14} />Give somebody their own
            </Button>
          </span>
        )}
        <span className="text-[12px] leading-[1.45] text-text-muted">
          A person’s own comes first, then the first of their roles in this list, then the company default.
        </span>
      </div>
      {adding && (
        <Popover open onClose={() => setAdding(false)} anchor={addAt.current} width={280} maxHeight={360}
          label="Give somebody their own" className="rounded-[12px] py-1">
          <div role="menu" aria-label="Give somebody their own" className="flex flex-col overflow-y-auto">
            {page.office.filter((person) => !listed.has(person.id)).map((person) => (
              <button key={person.id} type="button" role="menuitem"
                onClick={() => {
                  setAdding(false)
                  onAct({ userId: person.id, signatureId: company?.id ?? null })
                }}
                className="mx-1 flex flex-col rounded-[9px] px-3 py-1.5 text-left hover:bg-hover">
                <b className="text-[13px]">{person.name}</b>
                {person.title && <span className="text-[11.5px] text-text-muted">{person.title}</span>}
              </button>
            ))}
          </div>
        </Popover>
      )}
    </SettingsCard>
  )
}

// ---------------------------------------------------------------------------
// Automatic emails (Aaron, Oct 25, answer 2.6)
// ---------------------------------------------------------------------------

const SALESPERSON = 'salesperson'

/** What an appointment confirmation and an invoice end with — each its own choice. */
function AutomaticEmails({ page, canEdit, onAct }: {
  page: EmailSignaturesPage
  canEdit: boolean
  onAct: (body: unknown) => void
}) {
  const valueOf = (setting: AutomaticEmailSetting): string =>
    (setting.choice === 'signature' && setting.signatureId ? setting.signatureId
      : setting.choice === SALESPERSON ? SALESPERSON : NONE)
  const choose = (setting: AutomaticEmailSetting, value: string | null) => {
    if (value === NONE || value === null) onAct({ kind: setting.kind, choice: 'none' })
    else if (value === SALESPERSON) onAct({ kind: setting.kind, choice: 'salesperson' })
    else onAct({ kind: setting.kind, choice: 'signature', signatureId: value })
  }
  const options = [
    { value: NONE, label: 'No signature' },
    { value: SALESPERSON, label: 'The salesperson’s own' },
    ...page.signatures.map((one) => ({ value: one.id, label: one.name })),
  ]
  return (
    <SettingsCard title="Automatic emails" sub="the emails Rowted sends for you">
      {page.automatic.map((setting) => (
        <Row key={setting.kind} label={setting.label} sub={setting.sub}>
          <SelectInput label={`${setting.label} signature`} value={valueOf(setting)} disabled={!canEdit}
            onChange={(value) => choose(setting, value)} options={options} />
        </Row>
      ))}
      <p className="m-0 px-[18px] py-3 text-[12px] leading-[1.45] text-text-muted">
        A signature picked here signs as the company: the job’s branch and the company, and nobody’s name — a line
        made only of a person’s fields is left out. The salesperson’s own is the job’s salesperson’s, as their own
        emails end; with nobody on the job, the company default, signed as the company.
      </p>
    </SettingsCard>
  )
}

function Row({ label, sub, children, after }: {
  label: string; sub?: string; children: React.ReactNode; after?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line-faint px-[18px] py-[11px]">
      <div className="flex w-[240px] shrink-0 flex-col gap-0.5 max-[700px]:w-full">
        <b className="text-[13px] font-bold text-text">{label}</b>
        {sub && <span className="text-[12px] text-text-muted">{sub}</span>}
      </div>
      <div className="min-w-[200px] flex-1">{children}</div>
      {after}
    </div>
  )
}

// ---------------------------------------------------------------------------
// New signature — from a template
// ---------------------------------------------------------------------------

function NewSignature({ values, onClose, onMade }: {
  values: Parameters<typeof renderSignature>[1]
  onClose: () => void
  onMade: (made: EmailSignatureView) => void | Promise<void>
}) {
  const [name, setName] = useState('')
  const [template, setTemplate] = useState<SignatureTemplate['key']>('simple')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const make = async () => {
    const chosen = SIGNATURE_TEMPLATES.find((one) => one.key === template)!
    setBusy(true)
    setProblem(null)
    try {
      const answer = await request<SignatureSaved>('/settings/email-signatures', {
        method: 'POST', body: { name: name.trim(), html: chosen.html, editor: 'visual' },
      })
      await onMade(answer.signature)
    } catch (cause) {
      setProblem(errorOf(cause, 'That did not save.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} width={620} title="New signature" subtitle="start from a template, then make it yours"
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void make()}>
            {busy ? 'Making…' : 'Make it'}
          </Button>
        </>
      )}>
      <div className="flex flex-col gap-3.5 px-[18px] py-4">
        <Field label="Name" required>
          {({ id, describedBy }) => (
            <TextInput id={id} describedBy={describedBy} autoFocus value={name} maxLength={80}
              placeholder="Sales signature"
              onChange={(event) => setName(event.target.value)} />
          )}
        </Field>
        <div role="radiogroup" aria-label="Template" className="grid grid-cols-2 gap-2.5 max-[560px]:grid-cols-1">
          {SIGNATURE_TEMPLATES.map((one) => (
            <button key={one.key} type="button" role="radio" aria-checked={one.key === template}
              onClick={() => setTemplate(one.key)}
              className={cn('flex flex-col gap-2 rounded-record border p-3 text-left',
                one.key === template ? 'border-accent-line bg-green-band-bg' : 'border-line hover:bg-hover')}>
              <span className="flex flex-col">
                <b className="text-[13px] font-bold text-text">{one.name}</b>
                <span className="text-[11.5px] text-text-muted">{one.sub}</span>
              </span>
              <span className="pointer-events-none block rounded-field border border-line-faint px-2 py-1.5">
                <SignaturePaper html={renderSignature(one.html, values)} title={`${one.name} template`} maxHeight={110} />
              </span>
            </button>
          ))}
        </div>
        {problem && <p role="alert" className="m-0 text-[12.5px] text-danger">{problem}</p>}
      </div>
    </Modal>
  )
}
